import React, { useMemo, useRef, useState } from "react";
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Link, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography } from "@mui/material";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import { authRequest } from "../services/auth";

const HEADERS = ["Employee ID", "Full Name", "Email", "Roles"];
const SAMPLE = [HEADERS, ["123456", "Sample User", "sample.user@cognizant.com", "General"]];

function csvCell(value) {
  const text = String(value || "");
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function parseCsv(text) {
  const rows = [];
  let row = [], cell = "", quoted = false;
  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    if (char === '"' && quoted && text[index + 1] === '"') { cell += '"'; index += 1; }
    else if (char === '"') quoted = !quoted;
    else if (char === "," && !quoted) { row.push(cell.trim()); cell = ""; }
    else if ((char === "\n" || char === "\r") && !quoted) {
      if (char === "\r" && text[index + 1] === "\n") index += 1;
      row.push(cell.trim());
      if (row.some(Boolean)) rows.push(row);
      row = []; cell = "";
    } else cell += char;
  }
  row.push(cell.trim());
  if (row.some(Boolean)) rows.push(row);
  if (!rows.length || HEADERS.some((header, index) => String(rows[0][index] || "").replace(/^\uFEFF/, "") !== header)) {
    throw new Error(`CSV headers must be: ${HEADERS.join(", ")}`);
  }
  return rows.slice(1).map((values, index) => ({
    key: `${Date.now()}-${index}`,
    userId: values[0] || "",
    name: values[1] || "",
    email: values[2] || "",
    roles: (values[3] || "General").split(/[;,|]/).map((role) => role.trim()).filter(Boolean),
  }));
}

function rowError(row) {
  if (!row.userId.trim()) return "Employee ID is required";
  if (!row.name.trim()) return "Full name is required";
  if (!/^[^\s@]+@cognizant\.com$/i.test(row.email.trim())) return "A valid @cognizant.com email is required";
  if (!row.roles.length) return "At least one role is required";
  return "";
}

export default function BulkUserDialog({ open, onClose, onComplete }) {
  const inputRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const errors = useMemo(() => rows.map(rowError), [rows]);

  const downloadTemplate = () => {
    const csv = SAMPLE.map((row) => row.map(csvCell).join(",")).join("\r\n");
    const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url; link.download = "bulk-user-template.csv"; link.click();
    URL.revokeObjectURL(url);
  };

  const upload = async (event) => {
    setError(""); setResult(null);
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const parsed = parseCsv(await file.text());
      if (!parsed.length) throw new Error("The CSV does not contain any users");
      if (parsed.length > 100) throw new Error("A maximum of 100 users can be uploaded at once");
      setRows(parsed);
    } catch (uploadError) { setRows([]); setError(uploadError.message); }
    event.target.value = "";
  };

  const update = (index, field, value) => setRows((current) => current.map((row, rowIndex) => rowIndex === index ? { ...row, [field]: field === "roles" ? value.split(/[;,|]/).map((role) => role.trim()).filter(Boolean) : value } : row));

  const submit = async () => {
    if (!rows.length || errors.some(Boolean)) { setError("Correct the highlighted rows before submitting."); return; }
    setSaving(true); setError(""); setResult(null);
    try {
      const { data } = await authRequest().post("/users/bulk", { users: rows.map(({ userId, name, email, roles }) => ({ userId, name, email, roles })) });
      setResult(data); onComplete?.();
    } catch (submitError) {
      if (submitError.response?.data?.results) { setResult(submitError.response.data); onComplete?.(); }
      else setError(submitError.response?.data?.error || "An error occurred while creating users");
    } finally { setSaving(false); }
  };

  const close = () => { if (!saving) { setRows([]); setResult(null); setError(""); onClose(); } };
  return <Dialog open={open} onClose={close} fullWidth maxWidth="lg">
    <DialogTitle sx={{ fontWeight: 800 }}>Bulk add users</DialogTitle>
    <DialogContent>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} sx={{ mb: 2 }}>
        <Button startIcon={<DownloadRoundedIcon />} variant="outlined" onClick={downloadTemplate} sx={{ textTransform: "none" }}>Download CSV template</Button>
        <Button startIcon={<UploadFileRoundedIcon />} variant="contained" onClick={() => inputRef.current?.click()} sx={{ textTransform: "none" }}>Upload completed CSV</Button>
        <input ref={inputRef} type="file" accept=".csv,text/csv" hidden onChange={upload} />
        <Typography variant="body2" color="text.secondary">Passwords are generated securely and emailed automatically. Email is mandatory.</Typography>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error} {rows.length > 0 && <Link component="button" onClick={() => setError("")} sx={{ ml: .5 }}>Review rows</Link>}</Alert>}
      {rows.length > 0 && <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 470, borderRadius: 2 }}><Table stickyHeader size="small">
        <TableHead><TableRow><TableCell>#</TableCell>{HEADERS.map((header) => <TableCell key={header}>{header}</TableCell>)}<TableCell /></TableRow></TableHead>
        <TableBody>{rows.map((row, index) => <TableRow key={row.key} sx={{ bgcolor: errors[index] ? "rgba(211,47,47,.05)" : undefined }}>
          <TableCell>{index + 1}</TableCell>
          <TableCell><TextField variant="standard" value={row.userId} onChange={(event) => update(index, "userId", event.target.value)} error={Boolean(errors[index]) && !row.userId} /></TableCell>
          <TableCell><TextField variant="standard" value={row.name} onChange={(event) => update(index, "name", event.target.value)} error={Boolean(errors[index]) && !row.name} /></TableCell>
          <TableCell><TextField variant="standard" type="email" value={row.email} onChange={(event) => update(index, "email", event.target.value)} error={Boolean(errors[index]) && !/^[^\s@]+@cognizant\.com$/i.test(row.email)} helperText={errors[index] || ""} /></TableCell>
          <TableCell><TextField variant="standard" value={row.roles.join(", ")} onChange={(event) => update(index, "roles", event.target.value)} helperText="Comma separated" /></TableCell>
          <TableCell><Tooltip title="Remove row"><IconButton color="error" onClick={() => setRows((current) => current.filter((_, rowIndex) => rowIndex !== index))}><DeleteOutlineRoundedIcon /></IconButton></Tooltip></TableCell>
        </TableRow>)}</TableBody>
      </Table></TableContainer>}
      {result && <Alert severity={result.failed ? "warning" : "success"} sx={{ mt: 2 }}>
        Created {result.created}; failed {result.failed}.
        <Box sx={{ mt: .75, display: "flex", gap: .75, flexWrap: "wrap" }}>{result.results.map((item) => <Chip key={`${item.row}-${item.userId}`} size="small" color={item.status === "created" && item.emailSent ? "success" : item.status === "failed" ? "error" : "warning"} label={`${item.userId || `Row ${item.row}`}: ${item.status === "failed" ? item.error : item.emailSent ? "created & emailed" : item.warning}`} />)}</Box>
      </Alert>}
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2.5 }}><Button onClick={close} disabled={saving}>{result ? "Close" : "Cancel"}</Button><Button onClick={submit} variant="contained" disabled={saving || !rows.length || Boolean(result)}>{saving ? <CircularProgress size={22} /> : `Create ${rows.length || ""} users`}</Button></DialogActions>
  </Dialog>;
}
